/**
 * Safe localStorage utility functions
 * Provides error handling for localStorage operations with TypeScript typing
 */

// Define generic types for strongly-typed localStorage operations
// Instead of constraining to Record<string, unknown>, we'll use a more flexible type
// that allows any object-like structure to be used with our utility

export const safeLocalStorage = {
  /**
   * Gets an item from localStorage with proper error handling
   * @param key The key to retrieve
   * @param fallback Default value if key doesn't exist or on error
   * @returns The parsed value or fallback
   */
  get: <T = unknown>(key: string, fallback: T = null as unknown as T): T => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) as T : fallback;
    } catch (e) {
      console.error(`Error reading ${key} from localStorage:`, e);
      return fallback;
    }
  },
  
  /**
   * Sets an item in localStorage with proper error handling
   * @param key The key to set
   * @param data The data to store
   * @returns Success status
   */
  set: <T = unknown>(key: string, data: T): boolean => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (e) {
      console.error(`Error writing ${key} to localStorage:`, e);
      return false;
    }
  },
  
  /**
   * Updates an existing item in localStorage using an updater function
   * @param key The key to update
   * @param updater Function that receives current value and returns new value
   * @param fallback Default value if key doesn't exist or on error
   * @returns Success status
   */
  update: <T = unknown>(key: string, updater: (prevData: T) => T, fallback: T = {} as unknown as T): boolean => {
    try {
      const prevData = safeLocalStorage.get<T>(key, fallback);
      const newData = updater(prevData);
      return safeLocalStorage.set(key, newData);
    } catch (e) {
      console.error(`Error updating ${key} in localStorage:`, e);
      return false;
    }
  }
};
